import {
    PartnerCockpitChargeStatus,
    type PartnerCockpitSubscriptionCharge,
} from '@houzy/schemas/partner-cockpit/subscription';
import {
    ChargeKind,
    isFreeTimeCharge,
    SubscriptionPaymentMethod,
    type SubscriptionStatus,
} from '@houzy/schemas/partner-management/partner-subscription-management';
import type { TFunction } from 'i18next';

function chargeTypeLabelKey(charge: PartnerCockpitSubscriptionCharge): string {
    switch (charge.kind) {
        case ChargeKind.Correction:
            // A document a correction can carry.
            return charge.invoiceNumber
                ? 'SETTINGS.SUBSCRIPTION.CHARGE_TYPE.CREDIT_NOTE'
                : 'SETTINGS.SUBSCRIPTION.CHARGE_TYPE.CORRECTION';
        case ChargeKind.CreditsCoverage:
            return 'SETTINGS.SUBSCRIPTION.CHARGE_TYPE.COVERED';
        case ChargeKind.EarlyCancellationFee:
            return 'SETTINGS.SUBSCRIPTION.CHARGE_TYPE.EARLY_CANCELLATION';
        case ChargeKind.Recurring:
        case ChargeKind.Upfront:
            return isFreeTimeCharge(charge)
                ? 'SETTINGS.SUBSCRIPTION.CHARGE_TYPE.FREE_TIME'
                : `SETTINGS.SUBSCRIPTION.CADENCE.${charge.billingCadence.toUpperCase()}`;
    }
}

export function subscriptionChargeTypeLabel(t: TFunction, charge: PartnerCockpitSubscriptionCharge): string {
    return t(chargeTypeLabelKey(charge));
}

export function subscriptionChargeStatusLabel(t: TFunction, status: PartnerCockpitChargeStatus): string {
    return t(`SETTINGS.SUBSCRIPTION.CHARGE_STATUS.${status.toUpperCase()}`);
}

export function subscriptionStatusLabel(t: TFunction, status: SubscriptionStatus): string {
    return t(`SETTINGS.SUBSCRIPTION.STATUS.${status.toUpperCase()}`);
}

export function subscriptionUnvalidatedTitle(t: TFunction, paymentMethod: SubscriptionPaymentMethod): string {
    return t(
        paymentMethod === SubscriptionPaymentMethod.Invoice
            ? 'SETTINGS.SUBSCRIPTION.UNVERIFIED.INVOICE_TITLE'
            : 'SETTINGS.SUBSCRIPTION.UNVERIFIED.STRIPE_TITLE',
    );
}

export function subscriptionCancelBody(t: TFunction, endsCoveredPeriod: boolean, date: string | undefined): string {
    const key = endsCoveredPeriod ? 'SETTINGS.SUBSCRIPTION.CANCEL.BODY_COVERED' : 'SETTINGS.SUBSCRIPTION.CANCEL.BODY';
    return date ? t(key, { date }) : t(key);
}

export function subscriptionScheduledCancellationBody(t: TFunction, endsCoveredPeriod: boolean, date: string): string {
    return t(
        endsCoveredPeriod
            ? 'SETTINGS.SUBSCRIPTION.SCHEDULED_CANCELLATION.BODY_COVERED'
            : 'SETTINGS.SUBSCRIPTION.SCHEDULED_CANCELLATION.BODY',
        { date },
    );
}

export const SUBSCRIPTION_CHARGE_STATUS_COLOR: Record<
    PartnerCockpitChargeStatus,
    'success' | 'warning' | 'error' | 'default'
> = {
    [PartnerCockpitChargeStatus.Paid]: 'success',
    [PartnerCockpitChargeStatus.Due]: 'warning',
    [PartnerCockpitChargeStatus.Overdue]: 'error',
    [PartnerCockpitChargeStatus.Refunded]: 'default',
    [PartnerCockpitChargeStatus.Cancelled]: 'default',
};

export function subscriptionBillingHintKey(
    paymentMethod: SubscriptionPaymentMethod,
    canManageBilling: boolean,
): string {
    if (paymentMethod === SubscriptionPaymentMethod.Invoice) {
        return 'SETTINGS.SUBSCRIPTION.BILLING.INVOICE_RAIL';
    }
    return canManageBilling
        ? 'SETTINGS.SUBSCRIPTION.BILLING.MANAGE_HINT'
        : 'SETTINGS.SUBSCRIPTION.BILLING.MANAGE_UNAVAILABLE';
}
